import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  activePushpinAttributesType,
  documentType,
  fieldIssueTypesType,
  idToTitleMapType,
  oxygenType,
  pendingIssueAttributesType,
  pendingIssueFormAttributesType,
  sheetObjectType
} from '../../constants/proptype-validation';
import {availableStatusesOnCreate, STATUS_OPEN, statusColorMapping} from '../../constants/status-types';
import {CREATE_ISSUE_FORM_LOCAL_STORAGE_KEY} from '../../constants/common';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import IssueCreateFormAttachmentsSectionContainer from './IssueCreateFormAttachmentsSectionContainer';
import {
  getInputValidationMessage,
  getIssueCustomAttributeInputValidationMessage,
  presenceValidator
} from '../../utils/validation-helpers';
import {getAllowedFiles} from '../../utils/attachmentsHelper';
import {getUserCompanyName} from '../../utils/userHelpers';
import {createIssue, getAssigneeTypeByAssigneeId} from '../../utils/analytics-helpers';
import {fromDateSelectorDate, toIsoDate} from '../../utils/date-helpers';
import {v4 as getUUID} from 'uuid';
import {getLocalStorageByKey} from '../../utils/localStorage-helpers';
import IssueCustomAttributeField from '../forms/IssueCustomAttributeField';
import Button from '@adsk/bim360-matrix-react-button';
import DatePicker from '@adsk/bim360-matrix-react-date-picker';
import DateSelector from '@adsk/bim360-matrix-react-date-selector';
import Dropzone from '@adsk/bim360-matrix-react-dropzone';
import ModalConfirmation from '@adsk/bim360-matrix-react-modal-confirmation';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import StickyScrollableContainer from '@adsk/bim360-matrix-react-sticky-scrollable-container';
import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import {Behaviors as TextInputBehaviors} from '@adsk/bim360-matrix-react-text-input';
import {WithScrollableContainerRef} from '@adsk/bim360-matrix-react-helpers';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import LinkToDocumentField from '../forms/LinkToDocumentField';
import {
  CREATE_FORM_FIELD_NAMES,
  CREATE_FORM_FIELD_NAMES_TO_KEEP_ON_CREATE_ANOTHER,
  CREATE_FORM_SEPARATOR
} from '../../constants/create-form';
import {attachmentOrigin, DM_URN_TYPE, OSS_URN_TYPE} from '../../constants/attachments';
import FormField from '../common/FormField';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';
import {camelizeProperties, decamelizeProperties} from '../../utils/jsonapi-helpers';
import IssueTypeSelectDropdownContainer from '../common/IssueTypeSelectDropdownContainer';
import IssueRootCauseDropdownContainer from './IssueRootCauseDropdownContainer';
import IssueLocationPickerContainer from './IssueLocationPickerContainer';
import TemplateSelectorContainer from '../forms/TemplateSelectorContainer';
import {getPushpinLocationId, hasPushpinLocationInfo} from '../../utils/pushpin-helpers';
import {formatStatusOptions} from '../../utils/issueCreate-helpers';
import {toV1Structure} from '../../utils/version-converters';
import {getNonEmpties} from '../../utils/issues-helpers';
import {constructLinkedDocument} from '../../utils/document-helpers';
import SelectBoxAdapter from '../common/SelectBoxAdapter';
import {
  addTemplateToRecentlyUsedAction,
  cancelAttachmentUpload,
  clearPendingThumbnail,
  clearUploadingAttachmentsListByOrigin,
  createAttachmentForCreate,
  createIssueAction,
  setIsCreatingIssueAction,
  showNotificationAction,
  trackEvent,
  uploadAttachmentFiles,
  viewerPushpinThumbnailCreated
} from '../../app/actions';
import {actions as formActions} from 'react-redux-form';
import {viewTypes} from '../../constants/view-types';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {locationChangeNotification} from '../../utils/notificationHelpers';
import {trim} from 'lodash';

const scrollableContainerClassName = 'IssueCreateForm__form-content-container';
const DatePickerWithContainer = WithScrollableContainerRef(DatePicker, scrollableContainerClassName);
const IssueTypeSelectDropdownScrollableContainer = WithScrollableContainerRef(
  IssueTypeSelectDropdownContainer,
  scrollableContainerClassName
);
const IssueRootCauseDropdownScrollableContainer = WithScrollableContainerRef(
  IssueRootCauseDropdownContainer,
  scrollableContainerClassName
);
const supportedAdditionalAttributes = Object.values(ISSUE_ATTRIBUTE_NAMES);

class IssueCreateForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = this.resetState(props);
    this._resetAndKeepPreviousNeededValues = this._resetAndKeepPreviousNeededValues.bind(this);
    this.resetState = this.resetState.bind(this);
    this.handleScrollableRef = this.handleScrollableRef.bind(this);
    this.getFieldErrorMessage = this.getFieldErrorMessage.bind(this);
    this.handleCustomAttributeFieldOnChange = this.handleCustomAttributeFieldOnChange.bind(this);
    this.handleGroupedIssueSubTypeChange = this.handleGroupedIssueSubTypeChange.bind(this);
    this.getWrappedLocalStorageKey = this.getWrappedLocalStorageKey.bind(this);

    this.requiredFieldMessage = i18n.t('new.fieldRequired');
    this.dropdownMaxWidth = '100%';

    this.fieldComponents = {};

    // Build status options list for create form
    const {availableStatuses} = this.props;
    const customAvailableStatuses =
      !isEmpty(availableStatuses) && availableStatusesOnCreate.filter(status => availableStatuses.includes(status));

    if (!isEmpty(customAvailableStatuses)) {
      this.statusOptions = formatStatusOptions(customAvailableStatuses);
    } else {
      this.statusOptions = formatStatusOptions(availableStatusesOnCreate);
    }

    this.fieldNames = this.getFieldNames(props);
  }

  componentDidMount() {
    this.setIssueDropdownDefaultValues();
    this.props.dispatch(setIsCreatingIssueAction(true));
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.issuePushpin !== nextProps.issuePushpin) {
      this.setState({issuePushpin: nextProps.issuePushpin});
    }
    if (this.props.currentSheet !== nextProps.currentSheet) {
      this.setState({currentSheet: nextProps.currentSheet});
    }
    if (this.props.updateDocumentDetails) {
      if (nextProps.document && this.state.document !== nextProps.document) {
        this.setState({document: nextProps.document});
      }
      if (nextProps.targetUrn && this.state.targetUrn !== nextProps.targetUrn) {
        this.setState({targetUrn: nextProps.targetUrn});
      }
    }
    if (this.props.anyActiveIssueTemplates !== nextProps.anyActiveIssueTemplates) {
      this.fieldNames = this.getFieldNames(nextProps);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.titleInput && this.props.focusForm && !prevProps.focusForm) {
      this.titleInput.focus();
    }
    if (
      this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID] !==
      prevProps.pendingIssue[ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID]
    ) {
      this.props.dispatch(formActions.reset('pendingIssue'));
      this.loadFormValuesFromTemplate();
      if (!this.props.allowLinkToDocument) {
        this.setLinkedDocumentField();
      }
    }
    if (
      prevState.issuePushpin !== this.state.issuePushpin &&
      this.state.issuePushpin &&
      hasPushpinLocationInfo(this.state.issuePushpin)
    ) {
      const prevPushpinLocationId = getPushpinLocationId(prevState.issuePushpin);
      const pushpinLocationId = getPushpinLocationId(this.state.issuePushpin);
      if (prevPushpinLocationId !== pushpinLocationId) {
        this.handleFieldOnChange(pushpinLocationId, ISSUE_ATTRIBUTE_NAMES.LOCATION_ID, {silent: true});
        featureFlags.get(FEATURE_FLAGS.UPDATE_LOCATION_WITH_PUSHPIN) &&
          this.props.dispatch(
            locationChangeNotification(this.props.locationsTreeNodesDictionary[pushpinLocationId]?.name)
          );
      }
    }
    if (
      prevState.issuePushpin !== this.state.issuePushpin ||
      prevState.document !== this.state.document ||
      prevState.targetUrn !== this.state.targetUrn ||
      prevState.currentSheet !== this.state.currentSheet
    ) {
      this.setLinkedDocumentField();
    }
  }

  componentWillUnmount() {
    this.props.dispatch(clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM));
    this.props.dispatch(setIsCreatingIssueAction(false));
  }

  getFieldNames(props) {
    return props.anyActiveIssueTemplates
      ? [ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID, CREATE_FORM_SEPARATOR, ...CREATE_FORM_FIELD_NAMES]
      : CREATE_FORM_FIELD_NAMES;
  }

  setLinkedDocumentField() {
    const issueDocument = constructLinkedDocument(this.state);
    const hasValue = issueDocument && Object.keys(issueDocument).length > 0;
    this.changeFormValue(
      // Stringifying the value because react-redux-form mishandles deep values
      hasValue ? JSON.stringify(issueDocument) : undefined,
      ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS,
      null,
      this.props.requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS],
      {silent: true}
    );
  }

  loadFormValuesFromTemplate() {
    const {formValuesFromTemplate, requiredFieldsMap, dispatch} = this.props;
    const clonedFormValuesFromTemplate = {...formValuesFromTemplate};
    const currentLocationId = getPushpinLocationId(this.state.issuePushpin);
    const isLocationFromPushpin =
      currentLocationId && currentLocationId === this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID];
    if (isLocationFromPushpin) {
      clonedFormValuesFromTemplate[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID] = currentLocationId;
    }

    Object.entries(clonedFormValuesFromTemplate).forEach(([fieldName, value]) => {
      if (fieldName === ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID) {
        this.handleIssueSubTypeChange(value); // Stop the earlier type's custom attributes from being required
      } else {
        const touched = getInputValidationMessage(fieldName, value);
        const isRequired = requiredFieldsMap[fieldName];
        this.changeFormValue(value, fieldName, touched, isRequired);
      }
    });

    const customAttributeMappings = this.getSubTypeCustomAttributeMappings(
      clonedFormValuesFromTemplate[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID] ||
        this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
    );
    this.getMandatoryFields(customAttributeMappings).forEach(fieldName => {
      if (!clonedFormValuesFromTemplate[fieldName]) {
        dispatch(
          formActions.setErrors(`pendingIssue.${fieldName}`, {
            submitFailed: this.requiredFieldMessage
          })
        );
      }
    });
  }

  resetState(props) {
    return {
      document: props.document,
      issuePushpin: props.issuePushpin,
      currentSheet: props.currentSheet,
      targetUrn: props.targetUrn
    };
  }

  handleScrollableRef(node) {
    this.scrollableElement = node;
  }

  handleSelectStatus(selectedStatus) {
    this.props.dispatch(formActions.change('pendingIssue.status', selectedStatus.status));
    this.props.dispatch(formActions.setErrors('pendingIssue.status', !presenceValidator(selectedStatus.status)));
  }

  changeFormValue(value, fieldName, touched, isRequired, options = {}) {
    const {dispatch} = this.props;
    let submitFailed;

    dispatch(formActions.change(`pendingIssue['${fieldName}']`, value, options));
    if (isRequired) {
      submitFailed = !presenceValidator(value) && this.requiredFieldMessage;
    }

    dispatch(formActions.setErrors(`pendingIssue['${fieldName}']`, {touched, submitFailed}));
  }

  handleFieldOnChange(value, fieldName, options) {
    const touched = getInputValidationMessage(fieldName, value);
    const isRequired = this.props.requiredFieldsMap[fieldName];

    this.changeFormValue(value, fieldName, touched, isRequired, options);
  }

  handleCustomAttributeFieldOnChange(value, fieldId, isRequired) {
    const {issueCustomAttributes} = this.props;
    const customAttribute = issueCustomAttributes[fieldId];
    const touched = getIssueCustomAttributeInputValidationMessage(customAttribute.dataType, value);

    this.changeFormValue(value, fieldId, touched, isRequired);
  }

  handleIssueSubTypeChange(issueSubTypeId) {
    const {dispatch, pendingIssue} = this.props;

    const prevRequiredCustomAttributes = this.getSubTypeCustomAttributeMappings(
      this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
    )
      .filter(mapping => mapping.isRequired)
      .map(mapping => mapping.attributeDefinitionId);
    const newSubTypeRequiredCustomAttributes = this.getSubTypeCustomAttributeMappings(issueSubTypeId)
      .filter(mapping => mapping.isRequired)
      .map(mapping => mapping.attributeDefinitionId);

    const noLongerRequiredCustomAttributes = prevRequiredCustomAttributes.filter(
      attrId => !newSubTypeRequiredCustomAttributes.includes(attrId)
    );
    const newRequiredCustomAttributes = newSubTypeRequiredCustomAttributes.filter(
      attrId => !prevRequiredCustomAttributes.includes(attrId)
    );
    noLongerRequiredCustomAttributes.forEach(attrId => {
      dispatch(formActions.setErrors(`pendingIssue.${attrId}`, {submitFailed: null}));
    });
    newRequiredCustomAttributes.forEach(attrId => {
      dispatch(
        formActions.setErrors(`pendingIssue.${attrId}`, {
          submitFailed: !presenceValidator(pendingIssue[attrId]) && this.requiredFieldMessage
        })
      );
    });

    this.handleFieldOnChange(issueSubTypeId, ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID);
  }

  handleGroupedIssueSubTypeChange(issueSubType) {
    this.handleIssueSubTypeChange(issueSubType.id);
  }

  getMandatoryFields(issueCustomAttributesMappings = []) {
    const {requiredFieldsMap} = this.props;
    const requiredCustomAttributes = issueCustomAttributesMappings
      .filter(mapping => mapping.isRequired)
      .map(mapping => mapping.attributeDefinitionId);
    const requiredFieldsList = Object.keys(requiredFieldsMap).filter(field => requiredFieldsMap[field]);

    return [...requiredFieldsList, ...requiredCustomAttributes];
  }

  handleSelectAssignee(assignee) {
    const {dispatch, requiredFieldsMap} = this.props;

    dispatch(formActions.change(`pendingIssue.${ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO}`, assignee && assignee.oxygenId));
    dispatch(
      formActions.change(`pendingIssue.${ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE}`, assignee && assignee.oxygenType)
    );

    dispatch(
      formActions.setErrors(`pendingIssue.${ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO}`, {
        submitFailed:
          requiredFieldsMap[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] &&
          !presenceValidator(assignee?.oxygenId) &&
          this.requiredFieldMessage
      })
    );
  }

  uploadFiles(filesArray) {
    this.props.dispatch(
      uploadAttachmentFiles(
        [...filesArray].map(file => ({id: getUUID(), urnType: OSS_URN_TYPE, name: file.name, file})),
        null,
        false,
        attachmentOrigin.CREATE_FORM
      )
    );
  }

  chooseDocsDocument(documentUrn, document) {
    this.props.dispatch(
      createAttachmentForCreate({
        document,
        fileId: documentUrn,
        id: documentUrn,
        urn: documentUrn,
        name: document.name || lodashGet(document, 'currentVersion.name'),
        urnType: DM_URN_TYPE,
        origin: attachmentOrigin.CREATE_FORM
      })
    );
  }

  onFileCancelClick(file) {
    this.props.dispatch(cancelAttachmentUpload(file.id));
  }

  getFieldErrorMessage(fieldName) {
    return (
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.touched`) ||
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.submitFailed`) ||
      undefined
    );
  }

  getWrappedLocalStorageKey(key) {
    const {localStoragePrefix} = this.props;
    const keyWrappedWithProjectAndUser = `${key}_${this.props.currentProject.id}_${this.props.analyticsId}`;
    return localStoragePrefix ? `${localStoragePrefix}_${keyWrappedWithProjectAndUser}` : keyWrappedWithProjectAndUser;
  }

  setLocalStorageKey(key, value) {
    const wrappedKey = this.getWrappedLocalStorageKey(key);
    localStorage.setItem(wrappedKey, JSON.stringify(value));
  }

  getLocalStorageByKeyWrapper(key, disableOldKeyFallback) {
    const wrappedKey = this.getWrappedLocalStorageKey(key);

    if (disableOldKeyFallback) {
      return getLocalStorageByKey(wrappedKey);
    }

    // TODO: BTLV-11899 - remove this block of code and disableOldKeyFallback parameter
    const newKeyValue = getLocalStorageByKey(wrappedKey);
    if (newKeyValue) {
      return newKeyValue;
    } else {
      // if the user has the old key then we are copying it to the new one
      const oldKeyValue = getLocalStorageByKey(key);
      if (oldKeyValue) {
        this.setLocalStorageKey(key, oldKeyValue);
      }
      return oldKeyValue;
    }
  }

  /**
   * This function gets the default subtypeId by the required precedence
   * @returns {*}
   */
  getIssueDefaultSubtypeId() {
    const {fieldIssueTypeEntitiesMap, initialFieldValues} = this.props;
    const isSubtypeActive = id => lodashGet(fieldIssueTypeEntitiesMap, `${id}.isActive`, false);

    // Trying to get subtypeId from initialFieldValues
    const issueSubtypeIdIntialValue = lodashGet(initialFieldValues, ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID);
    if (issueSubtypeIdIntialValue && isSubtypeActive(issueSubtypeIdIntialValue)) {
      return issueSubtypeIdIntialValue;
    }

    let previousIssueConfigFromLocalStorage = this.getLocalStorageByKeyWrapper(
      CREATE_ISSUE_FORM_LOCAL_STORAGE_KEY,
      !!this.props.localStoragePrefix
    );
    // We convert back and forth between v1 and v2 to catch all v1 oddities (client has an old localStorage, for example)
    if (previousIssueConfigFromLocalStorage) {
      previousIssueConfigFromLocalStorage = camelizeProperties(
        decamelizeProperties(previousIssueConfigFromLocalStorage)
      );
    }

    // Trying to get subtypeId from localStorage
    const issueSubtypeIdFromLocalStorage =
      previousIssueConfigFromLocalStorage &&
      previousIssueConfigFromLocalStorage[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID];
    if (issueSubtypeIdFromLocalStorage && isSubtypeActive(issueSubtypeIdFromLocalStorage)) {
      return issueSubtypeIdFromLocalStorage;
    }

    // Trying to get subtypeId from defaultIssueSubtypeId
    const issueSubtypeIdFromProps = this.props.defaultIssueSubtypeId;
    if (issueSubtypeIdFromProps && isSubtypeActive(issueSubtypeIdFromProps)) {
      return issueSubtypeIdFromProps;
    }

    return this.props.systemDefaultIssueSubtypeId;
  }

  setIssueDropdownDefaultValues() {
    const {initialFieldValues, dispatch, issuePushpin, requiredFieldsMap} = this.props;
    const issueSubtypeId = this.getIssueDefaultSubtypeId();

    const predefinedData = {
      [ISSUE_ATTRIBUTE_NAMES.STATUS]: STATUS_OPEN,
      [ISSUE_ATTRIBUTE_NAMES.OWNER_ID]: this.props.currentUserId,
      [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: issueSubtypeId
    };

    if (initialFieldValues) {
      // We handle subtype initial value inside `getIssueDefaultSubtypeId` - as we need to also verify the subtype is active.
      Object.keys(initialFieldValues)
        .filter(f => f !== ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID)
        .forEach(field => {
          predefinedData[field] = initialFieldValues[field];
        });
    }

    const pushpinLocationId = getPushpinLocationId(issuePushpin);
    if (pushpinLocationId) {
      predefinedData[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID] = pushpinLocationId;
    }

    const predefinedFieldNames = Object.keys(predefinedData);
    predefinedFieldNames.forEach(fieldName => {
      const value = predefinedData[fieldName];
      const touched = getInputValidationMessage(fieldName, value);
      const isRequired = requiredFieldsMap[fieldName];

      this.changeFormValue(value, fieldName, touched, isRequired, {silent: true});
    });

    const customAttributeMappings = this.getSubTypeCustomAttributeMappings(issueSubtypeId);
    this.getMandatoryFields(customAttributeMappings).forEach(fieldName => {
      if (!predefinedFieldNames.includes(fieldName)) {
        dispatch(
          formActions.setErrors(`pendingIssue.${fieldName}`, {
            submitFailed: this.requiredFieldMessage
          })
        );
      }
    });
  }

  getSubTypeCustomAttributeMappings(subTypeId) {
    const {issueCustomAttributesMappings, fieldIssueTypeEntitiesMap} = this.props;
    const currentSubType = fieldIssueTypeEntitiesMap[subTypeId];
    if (currentSubType) {
      return currentSubType.mappingIds.map(attribute => issueCustomAttributesMappings[attribute]);
    }
    return [];
  }

  getSubTypeCustomAttributes(subTypeId) {
    const {issueCustomAttributes} = this.props;
    return this.getSubTypeCustomAttributeMappings(subTypeId).map(
      mapping => issueCustomAttributes[mapping.attributeDefinitionId]
    );
  }

  getPendingCustomIssueAttributes() {
    const {pendingIssue} = this.props;
    const subTypeCustomAttributes = this.getSubTypeCustomAttributes(
      pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
    );

    return subTypeCustomAttributes.reduce((acc, customAttribute) => {
      const customAttributeId = customAttribute.id;
      let pendingCustomAttribute = pendingIssue[customAttributeId];
      if (pendingCustomAttribute) {
        if (customAttribute.dataType === ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC) {
          const parsedValue = parseFloat(pendingCustomAttribute);
          pendingCustomAttribute = isNaN(parsedValue) ? pendingCustomAttribute : parsedValue;
        } else if (
          [ISSUE_ATTRIBUTES_DATA_TYPES.TEXT, ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH].includes(customAttribute.dataType)
        ) {
          pendingCustomAttribute = trim(pendingCustomAttribute);
        }
        acc.push({attributeDefinitionId: customAttributeId, value: pendingCustomAttribute});
      }
      return acc;
    }, []);
  }

  getIssueAttributes() {
    const {pendingIssue, fieldIssueTypeEntitiesMap, additionalAttributes = {}, handleSave} = this.props;
    const {
      [ISSUE_ATTRIBUTE_NAMES.STATUS]: status,
      [ISSUE_ATTRIBUTE_NAMES.TITLE]: title,
      [ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]: description,
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]: locationDetails,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: assignedTo,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: assignedToType,
      [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: dueDate,
      [ISSUE_ATTRIBUTE_NAMES.OWNER_ID]: ownerId,
      [ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID]: rootCauseId,
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]: locationId,
      [ISSUE_ATTRIBUTE_NAMES.ISSUE_TEMPLATE_ID]: issueTemplateId,
      [ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS]: serializedIssueDocument
    } = pendingIssue;
    const subTypeId = pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID];
    const subType = fieldIssueTypeEntitiesMap[subTypeId];
    // Create object from non-empty values
    const issueAttributes = getNonEmpties({
      [ISSUE_ATTRIBUTE_NAMES.TITLE]: trim(title),
      [ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]: trim(description),
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]: trim(locationDetails),
      [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: dueDate,
      [ISSUE_ATTRIBUTE_NAMES.ISSUE_TEMPLATE_ID]: issueTemplateId,
      [ISSUE_ATTRIBUTE_NAMES.STATUS]: status,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: assignedTo,
      [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: assignedToType,
      [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: subType.parentId,
      [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: subTypeId,
      [ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES]: this.getPendingCustomIssueAttributes(),
      [ISSUE_ATTRIBUTE_NAMES.OWNER_ID]: ownerId,
      [ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID]: rootCauseId,
      [ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]: locationId
    });

    const issueDocument = serializedIssueDocument ? JSON.parse(serializedIssueDocument) : null;

    let foundUnsupported = false;
    Object.entries(additionalAttributes).forEach(([key, value]) => {
      if (!supportedAdditionalAttributes.includes(key)) {
        if (!handleSave) {
          console.error(`Unsupported additional issue attribute: ${key}`);
          foundUnsupported = true;
        }
        return;
      }
      issueAttributes[key] = value;
    });
    if (foundUnsupported) {
      console.error(`Supported attributes: ${supportedAdditionalAttributes.join(', ')}`);
    }
    if (!isEmpty(issueDocument)) {
      issueAttributes[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS] = [issueDocument];
    }

    return issueAttributes;
  }

  _resetAndKeepPreviousNeededValues() {
    const savedState = {};
    CREATE_FORM_FIELD_NAMES_TO_KEEP_ON_CREATE_ANOTHER.forEach(fieldName => {
      savedState[fieldName] = this.props.pendingIssue[fieldName];
    });
    const customAttributeMappings = this.getSubTypeCustomAttributeMappings(
      this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
    );

    const statusOptions = this.statusOptions;
    const selectedStatus = statusOptions.find(status => status.status === this.props.pendingIssue.status);
    const savedStateFieldNames = Object.keys(savedState);

    this.setState(this.resetState(this.props));

    this.props.dispatch(formActions.reset('pendingIssue'));
    savedStateFieldNames.forEach(key => {
      if (key === ISSUE_ATTRIBUTE_NAMES.STATUS) {
        this.handleSelectStatus(selectedStatus);
      } else {
        this.props.dispatch(formActions.change(`pendingIssue.${key}`, savedState[key]));
        this.props.dispatch(formActions.setErrors(`pendingIssue.${key}`, {submitFailed: null}));
      }
    });

    this.getMandatoryFields(customAttributeMappings).forEach(fieldName => {
      if (!savedStateFieldNames.includes(fieldName)) {
        this.props.dispatch(
          formActions.setErrors(`pendingIssue.${fieldName}`, {
            submitFailed: this.requiredFieldMessage
          })
        );
      }
    });
    this.props.dispatch(clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM));
    if (this.scrollableElement) {
      this.scrollableElement.scrollTop = 0;
    }
  }

  saveIssue() {
    if (this.props.pendingIssueForm.valid && !this.props.isUploadingAttachments) {
      const {
        onSaveStart,
        onSaveSuccess,
        onSaveError,
        handleSave,
        pendingIssue,
        dispatch,
        origin,
        uploadingAttachments,
        currentUserId,
        issueTypesAndSubTypesTitleMap,
        oxygen,
        fieldIssueTypeEntitiesMap,
        additionalAttributes = {}
      } = this.props;
      const {assignedTo, status} = pendingIssue;
      const {document, issuePushpin} = this.state;
      const issueAttributes = this.getIssueAttributes();

      if (handleSave) {
        // Means that the saving process is done by an external app - need to provide the data as we send it to BE.
        const formattedIssue = toV1Structure(issueAttributes);
        // We skip over supported attributes since it means they've already been converted by toV1Structure
        Object.entries(additionalAttributes).forEach(([key, value]) => {
          if (supportedAdditionalAttributes.includes(key)) {
            return;
          }
          formattedIssue[key] = value;
        });
        handleSave(formattedIssue);
        return;
      }

      //MixPanel Event - when user create new issue
      const subTypeId = pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID];
      const subType = fieldIssueTypeEntitiesMap[subTypeId];
      const issueTypeTitle = issueTypesAndSubTypesTitleMap[subType.parentId];
      const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[subTypeId];
      const currentAssignedToType = pendingIssue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE];
      const assignee = getAssigneeTypeByAssigneeId(assignedTo, currentUserId, oxygen, currentAssignedToType);
      const numberOfCustomAttributes = lodashGet(subType, 'mappingIds.length', 0);
      const numberOfUsedCustomAttributes = lodashGet(
        issueAttributes,
        `${ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES}.length`,
        0
      );
      const isFromTemplate = !!pendingIssue[ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID];

      //Save the default value of IssueTypeId/IssueSubtypeId in local storage so we will know what the user picked last for next use.
      this.setLocalStorageKey(CREATE_ISSUE_FORM_LOCAL_STORAGE_KEY, {
        [ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]: this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID],
        [ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]: this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
      });

      const extra = {};
      extra.onSuccess = successResponse => {
        if (this.props.createAnother) {
          this._resetAndKeepPreviousNeededValues();
        }
        if (onSaveSuccess) {
          onSaveSuccess(successResponse);
        }
        dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: NotificationLevels.SUCCESS,
            message: i18n.t('new.alert.created', {issueType: TYPE_QUALITY_ISSUES})
          })
        );
        dispatch(clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM));

        dispatch(
          trackEvent(
            createIssue(
              document,
              status,
              origin,
              issuePushpin,
              uploadingAttachments,
              issueTypeTitle,
              issueSubTypeTitle,
              assignee,
              this.props.createAnother,
              numberOfCustomAttributes,
              numberOfUsedCustomAttributes,
              isFromTemplate
            )
          )
        );
      };
      if (onSaveError) {
        extra.onError = onSaveError;
      }
      if (onSaveStart) {
        onSaveStart();
      }
      extra.isDeepLinking = this.props.isCreateFormDeepLinking;
      if (issuePushpin) {
        // Include local pushpin ID in the action's data (we don't send it to the BE but
        // need it for managing pushpin snapshot upload)
        extra.issuePushpinId = issuePushpin.id;
      }
      this.props.dispatch(createIssueAction(issueAttributes, extra));
    } else {
      this.props.dispatch(formActions.setSubmitFailed('pendingIssue', true));
      if (!this.props.pendingIssueForm.valid) {
        const requiredCustomAttributeIds = this.getSubTypeCustomAttributeMappings(
          this.props.pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
        )
          .filter(mapping => mapping.isRequired)
          .map(mapping => mapping.attributeDefinitionId);
        const allFormFieldNames = CREATE_FORM_FIELD_NAMES.concat(requiredCustomAttributeIds);
        const firstErrorFieldName = allFormFieldNames.find(f => this.getFieldErrorMessage(f));
        if (firstErrorFieldName) {
          this.fieldComponents[firstErrorFieldName].scrollIntoView();
        }
      }
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    this.saveIssue();
  }

  shouldShowFieldError(fieldName) {
    // SubmitFailed - refers to errors that should be shown only after failing to submit form (missing required fields for example).
    // Touched - refers to a field which the user inserted value with errors (for example - too long description).
    const submitFailedCheck =
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].submitFailed`) &&
      lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.submitFailed`);
    const touchedCheck = lodashGet(this.props, `pendingIssueFormAttributes['${fieldName}'].errors.touched`);
    return !!submitFailedCheck || !!touchedCheck;
  }

  getFormFieldProps(fieldName) {
    return {
      key: fieldName,
      errorMessageText: this.getFieldErrorMessage(fieldName),
      showFieldError: this.shouldShowFieldError(fieldName),
      isFieldMandatory: this.props.requiredFieldsMap[fieldName],
      fieldRef: node => {
        this.fieldComponents[fieldName] = node;
      }
    };
  }

  renderAssignTo() {
    const {pendingIssue, disabled, groupedProjectEntities} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO;
    const textLabelAssignedTo = i18n.t('new.assignedTo');
    const baseProps = this.getFormFieldProps(fieldName);
    const textPlaceholderAssignedTo = i18n.t('new.placeholder.assignedTo');

    const assignedToOptions = groupedProjectEntities;
    const assignToFlatOptions = assignedToOptions.reduce((acc, item) => [...acc, ...item[1]], []);

    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__assignedTo" labelText={textLabelAssignedTo}>
        <SelectBoxAdapter
          options={assignedToOptions}
          selectedOptions={assignToFlatOptions.find(option => option.id === pendingIssue.assignedTo) ?? ''}
          grouped
          valid={!baseProps.showFieldError}
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          placeholder={textPlaceholderAssignedTo}
          className="IssueCreateForm__field-width"
          disabled={disabled}
          onChange={value => this.handleSelectAssignee(value)}
          searchable
          searchIcon
          searchPlaceholder={i18n.t('new.search')}
          clearable
        />
      </FormField>
    );
  }

  handleSelectDocumentPushpin({document, issuePushpin, currentSheet, thumbnail}) {
    this.setState({document, issuePushpin, targetUrn: document.urn, currentSheet});
    // Start snapshot upload flow
    if (thumbnail) {
      this.props.dispatch(
        viewerPushpinThumbnailCreated({
          pushpinAttributes: issuePushpin,
          thumbnail
        })
      );
    }
  }

  handleRemoveDocumentPushpin() {
    const pushpinId = lodashGet(this.state, 'issuePushpin.id');
    this.setState({document: null, issuePushpin: null, targetUrn: null, currentSheet: null});

    // Clear pending thumbnail
    if (pushpinId) {
      this.props.dispatch(clearPendingThumbnail(pushpinId));
    }
  }

  renderIssueSubtypeId() {
    const {disabled, pendingIssue} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID;
    const textLabelType = i18n.t('new.issueTypeAndSubType');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__subtypeId" labelText={textLabelType}>
        <IssueTypeSelectDropdownScrollableContainer
          className="IssueCreateForm__field-width"
          valid={!baseProps.showFieldError}
          disabled={disabled}
          selectedOptionId={pendingIssue[fieldName]}
          onChange={this.handleGroupedIssueSubTypeChange}
          dropdownMaxWidth={this.dropdownMaxWidth}
        />
      </FormField>
    );
  }

  renderStatus() {
    const {pendingIssue, disabled} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.STATUS;

    const textLabelStatus = i18n.t('new.status');
    const statusOptions = this.statusOptions;
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__status" labelText={textLabelStatus}>
        <SelectBoxAdapter
          selectedOptions={statusOptions.find(option => option.status === pendingIssue.status)}
          options={statusOptions}
          valid={!baseProps.showFieldError}
          optionRenderer={(value, label) => (
            <StatusColorIndicatorOption
              statusColor={statusColorMapping[statusOptions.find(option => option.value === value).status]}
              label={label}
            />
          )}
          valueRenderer={(value, label) => (
            <StatusColorIndicatorOption
              disabled={disabled}
              statusColor={statusColorMapping[statusOptions.find(option => option.value === value).status]}
              label={label}
            />
          )}
          className="IssueCreateForm__field-width"
          disabled={disabled}
          onChange={event => this.handleSelectStatus(event)}
          clearable={false}
        />
      </FormField>
    );
  }

  renderTitle() {
    const {pendingIssue, disabled, titleFieldLabel} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.TITLE;
    const textLabelTitle = titleFieldLabel ?? i18n.t('new.title');
    const baseProps = this.getFormFieldProps(fieldName);

    /* eslint-disable no-return-assign*/
    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__title" labelText={textLabelTitle}>
        <TextFieldCore
          ref={input => (this.titleInput = input)}
          valid={!baseProps.showFieldError}
          behavior={TextInputBehaviors.SINGLE_LINE}
          value={pendingIssue.title}
          onChange={value => this.handleFieldOnChange(value, fieldName)}
          disabled={disabled}
          className="IssueCreateForm__field-width"
        />
      </FormField>
    );
    /* eslint-enable no-return-assign*/
  }

  renderDescription() {
    const {pendingIssue, disabled} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DESCRIPTION;
    const textLabel = i18n.t('new.description');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__description" labelText={textLabel}>
        <TextFieldCore
          behavior={TextInputBehaviors.MULTI_LINE}
          value={pendingIssue.description}
          valid={!baseProps.showFieldError}
          onChange={value => this.handleFieldOnChange(value, fieldName)}
          disabled={disabled}
          className="IssueCreateForm__multi-line-text-field IssueCreateForm__field-width"
          data-testid="IssueCreateForm__description"
        />
      </FormField>
    );
  }

  renderAttachments() {
    const {disabled, container, isSubmitting, isUploadingAttachments, pendingIssueForm} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ATTACHMENTS_ATTRIBUTES;
    const showFieldError = pendingIssueForm.submitFailed && isUploadingAttachments;
    const textLabelAttachments = i18n.t('new.attachments');

    return (
      <FormField
        key={fieldName}
        labelText={textLabelAttachments}
        errorMessageText={i18n.t('new.error.attachmentUploading')}
        data-testid="IssuesCreateForm__attachments"
        showFieldError={showFieldError}
        isFieldMandatory={false}
      >
        <IssueCreateFormAttachmentsSectionContainer
          disabled={disabled}
          uploadFiles={this.uploadFiles.bind(this)}
          chooseDocsDocument={this.chooseDocsDocument.bind(this)}
          onFileCancelClick={this.onFileCancelClick.bind(this)}
          canCancelFiles={!isSubmitting}
          container={container}
        />
      </FormField>
    );
  }

  renderLinkToDocument() {
    const {container, allowLinkToDocument, pendingIssue} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS;
    const baseProps = this.getFormFieldProps(fieldName);
    const textLabelLinkToDocument = i18n.t('new.linkToDocument');

    if (allowLinkToDocument) {
      return (
        <FormField {...baseProps} labelText={textLabelLinkToDocument} data-testid="IssueCreateForm__link-to-document">
          <LinkToDocumentField
            container={container}
            status={pendingIssue.status}
            document={this.state.document}
            onSelectDocumentPushpin={this.handleSelectDocumentPushpin.bind(this)}
            onRemoveDocument={this.handleRemoveDocumentPushpin.bind(this)}
          />
        </FormField>
      );
    }
    return null;
  }

  renderLocation() {
    const {disabled, pendingIssue} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.LOCATION_ID;
    const textLabelLocation = i18n.t('new.location');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelLocation} data-testid="IssueCreateForm__lbs-location">
        <IssueLocationPickerContainer
          selectedEventKey={pendingIssue[ISSUE_ATTRIBUTE_NAMES.LOCATION_ID]}
          valid={!baseProps.showFieldError}
          disabled={disabled}
          onChange={event => this.handleFieldOnChange(event ? event.id : null, fieldName)}
        />
      </FormField>
    );
  }

  renderLocationDescription() {
    const {pendingIssue, disabled} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS;
    const textLabelLocation = i18n.t('new.locationDetails');
    const textPlaceholderLocation = i18n.t('new.placeholder.locationDetails');

    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelLocation} data-testid="IssueCreateForm__location-description">
        <TextFieldCore
          behavior={TextInputBehaviors.SINGLE_LINE}
          value={pendingIssue[ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS]}
          valid={!baseProps.showFieldError}
          onChange={value => this.handleFieldOnChange(value, fieldName)}
          placeholder={textPlaceholderLocation}
          disabled={disabled}
          className="IssueCreateForm__field-width"
          data-testid="IssueCreateForm__location-description"
        />
      </FormField>
    );
  }

  renderDueDate() {
    const {pendingIssue, disabled, isDateSelectorMigrationEnabled} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.DUE_DATE;
    const textLabelDueDate = i18n.t('new.dueDate');
    const textPlaceholderDueDate = i18n.t('new.placeholder.dueDate');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelDueDate} data-testid="IssueCreateForm__due-date">
        {isDateSelectorMigrationEnabled && (
          <DateSelector
            inputProps={{
              disablePastDates: true,
              placeholder: textPlaceholderDueDate,
              closedStatePlaceholder: textPlaceholderDueDate
            }}
            popupProps={{
              disablePastDates: true
            }}
            valid={!baseProps.showFieldError}
            date={pendingIssue.dueDate ? new Date(pendingIssue.dueDate) : null}
            onChange={({date}) => {
              this.handleFieldOnChange(fromDateSelectorDate(date), fieldName);
            }}
            className="IssueCreateForm__field-width"
            disabled={disabled}
            popupContainer={this.scrollableElement}
          />
        )}
        {!isDateSelectorMigrationEnabled && (
          <DatePickerWithContainer
            disablePastDates
            valid={!baseProps.showFieldError}
            date={pendingIssue.dueDate ? pendingIssue.dueDate : null}
            onChange={value => {
              this.handleFieldOnChange(toIsoDate(value), fieldName);
            }}
            className="IssueCreateForm__field-width"
            placeholder={textPlaceholderDueDate}
            disabled={disabled}
          />
        )}
      </FormField>
    );
  }

  renderOwner() {
    const {pendingIssue, disabled, users} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.OWNER_ID;

    const textLabelOwner = i18n.t('new.owner');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={textLabelOwner} data-testid="IssueCreateForm__owner">
        <SelectBoxAdapter
          selectedOptions={users.find(user => user.oxygenId === pendingIssue[ISSUE_ATTRIBUTE_NAMES.OWNER_ID])}
          options={users}
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={this.dropdownMaxWidth}
          subtextGetter={getUserCompanyName}
          valid={!baseProps.showFieldError}
          className="IssueCreateForm__field-width"
          disabled={disabled}
          onChange={user => this.handleFieldOnChange(user.oxygenId, fieldName)}
          clearable={false}
          searchable
          searchIcon
          searchPlaceholder={i18n.t('new.search')}
        />
      </FormField>
    );
  }

  renderRootCause() {
    const {disabled, pendingIssue} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID;
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} labelText={i18n.t('new.rootCause')} data-testid="IssueCreateForm__root-cause">
        <IssueRootCauseDropdownScrollableContainer
          className="IssueCreateForm__field-width"
          valid={!baseProps.showFieldError}
          disabled={disabled}
          selectedOptionId={pendingIssue.rootCauseId}
          onChange={option => this.handleFieldOnChange(option && option.id, fieldName)}
          clearable
        />
      </FormField>
    );
  }

  renderField(fieldName) {
    switch (fieldName) {
      case ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID:
        return this.renderTemplateSection();
      case ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID:
        return this.renderIssueSubtypeId();
      case ISSUE_ATTRIBUTE_NAMES.STATUS:
        return this.renderStatus();
      case ISSUE_ATTRIBUTE_NAMES.TITLE:
        return this.renderTitle();
      case ISSUE_ATTRIBUTE_NAMES.DESCRIPTION:
        return this.renderDescription();
      case ISSUE_ATTRIBUTE_NAMES.ATTACHMENTS_ATTRIBUTES:
        return this.renderAttachments();
      case ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS:
        return this.renderLinkToDocument();
      case ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO:
        return this.renderAssignTo();
      case ISSUE_ATTRIBUTE_NAMES.LOCATION_ID:
        return this.renderLocation();
      case ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS:
        return this.renderLocationDescription();
      case ISSUE_ATTRIBUTE_NAMES.DUE_DATE:
        return this.renderDueDate();
      case ISSUE_ATTRIBUTE_NAMES.OWNER_ID:
        return this.renderOwner();
      case ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID:
        return this.renderRootCause();
      case ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES:
        return this.renderIssueCustomAttributes();
      default:
        return null;
    }
  }

  renderSeparator(key) {
    return <div key={key} className="IssueCreateForm__separator" />;
  }

  renderCreateComponent(fieldName, index) {
    switch (fieldName) {
      case CREATE_FORM_SEPARATOR:
        return this.renderSeparator(index);
      default:
        return this.renderField(fieldName);
    }
  }

  renderIssueCustomAttributes() {
    const {pendingIssue, disabled, issueCustomAttributes} = this.props;
    const customAttributeMappings = this.getSubTypeCustomAttributeMappings(
      pendingIssue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]
    );
    if (customAttributeMappings.length) {
      return [
        this.renderSeparator('IssueCustomAttributesSeparator'),
        ...customAttributeMappings.map(mapping => {
          const isRequired = mapping.isRequired;
          const customAttribute = issueCustomAttributes[mapping.attributeDefinitionId];
          const pendingCustomAttribute = pendingIssue[customAttribute.id];
          const showFieldError = this.shouldShowFieldError(customAttribute.id);
          const errorMessage = this.getFieldErrorMessage(customAttribute.id);
          return (
            <IssueCustomAttributeField
              key={customAttribute.id}
              fieldRef={node => {
                this.fieldComponents[customAttribute.id] = node;
              }}
              value={pendingCustomAttribute}
              disabled={disabled}
              onChange={(value, fieldId) => this.handleCustomAttributeFieldOnChange(value, fieldId, isRequired)}
              showFieldError={showFieldError}
              errorMessage={errorMessage}
              customAttribute={customAttribute}
              isRequired={isRequired}
              container={this.scrollableElement}
            />
          );
        })
      ];
    }
    return null;
  }

  onTemplateSelected = template => {
    const templateId = template && template.id;
    if (this.props.pendingIssueForm.pristine) {
      this.setTemplate(templateId);
    } else {
      this.setState({
        isTemplateConfirmationOpen: true,
        templateValueToConfirm: templateId
      });
    }
  };

  setTemplate(templateId) {
    this.handleFieldOnChange(templateId, ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID);
    this.props.dispatch(addTemplateToRecentlyUsedAction(templateId));
  }

  renderTemplateSection() {
    const {pendingIssue, disabled} = this.props;
    const fieldName = ISSUE_ATTRIBUTE_NAMES.TEMPLATE_ID;
    const textLabel = i18n.t('new.useTemplate');
    const baseProps = this.getFormFieldProps(fieldName);

    return (
      <FormField {...baseProps} data-testid="IssueCreateForm__template" labelText={textLabel}>
        <TemplateSelectorContainer
          disabled={disabled}
          selectedTemplateId={pendingIssue[fieldName]}
          onSelect={this.onTemplateSelected}
        />
      </FormField>
    );
  }

  onTemplateChangeConfirmed = () => {
    this.setTemplate(this.state.templateValueToConfirm);

    const resetDerivedState = this.props.viewType === viewTypes.PROJECT ? this.resetState({}) : null;

    this.setState({
      ...resetDerivedState,
      templateValueToConfirm: undefined,
      isTemplateConfirmationOpen: false
    });
  };

  onTemplateChangeCanceled = () => {
    this.setState({
      templateValueToConfirm: undefined,
      isTemplateConfirmationOpen: false
    });
  };

  renderTemplateModalConfirmation() {
    return (
      this.state.isTemplateConfirmationOpen && (
        <ModalConfirmation
          title={i18n.t('createFromTemplate.confirmTitle')}
          message={i18n.t('createFromTemplate.confirmMessage')}
          onConfirm={this.onTemplateChangeConfirmed}
          onExited={this.onTemplateChangeCanceled}
          confirmationButtonLabel={i18n.t('createFromTemplate.yes')}
          cancelButtonLabel={i18n.t('createFromTemplate.no')}
          confirmationButtonStyleType={Button.StyleType.PRIMARY}
          container={this.props.container}
        />
      )
    );
  }

  render() {
    const {disabled, isSubmitting} = this.props;
    const allowFileUpload = false; // TODO - change this once we allow file uploading on create
    const enableFileUpload = allowFileUpload && !disabled && !isSubmitting;

    return (
      <Fragment>
        {this.renderTemplateModalConfirmation()}
        <StickyScrollableContainer className="IssueCreateForm">
          <StickyScrollableContainer.Header />
          <StickyScrollableContainer.Content className="IssueCreateForm__content">
            <form
              onSubmit={this.handleSubmit.bind(this)}
              className="IssueCreateForm__form"
              data-testid="IssueCreateForm"
            >
              <Dropzone onDrop={this.uploadFiles.bind(this)} accept={getAllowedFiles()} disabled={!enableFileUpload}>
                <div className={scrollableContainerClassName} ref={this.handleScrollableRef}>
                  <div className="IssueCreateForm__form-content">
                    {this.fieldNames.map(this.renderCreateComponent.bind(this))}
                  </div>
                </div>
              </Dropzone>
            </form>
          </StickyScrollableContainer.Content>
        </StickyScrollableContainer>
      </Fragment>
    );
  }
}

IssueCreateForm.displayName = 'IssueCreateForm';

IssueCreateForm.propTypes = {
  disabled: PropTypes.bool.isRequired,
  createAnother: PropTypes.bool,
  allowLinkToDocument: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onSaveStart: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  onSaveError: PropTypes.func,
  handleSave: PropTypes.func, // Override by an external app - to hijack create process.
  oxygen: oxygenType.isRequired,
  currentUserId: PropTypes.string.isRequired,
  currentProject: PropTypes.object.isRequired,
  analyticsId: PropTypes.string.isRequired,
  pendingIssue: pendingIssueAttributesType,
  pendingIssueForm: pendingIssueFormAttributesType.isRequired,
  pendingIssueFormAttributes: PropTypes.object.isRequired,
  issuePushpin: activePushpinAttributesType,
  document: documentType,
  targetUrn: PropTypes.string,
  origin: PropTypes.string,
  fieldIssueTypeEntitiesMap: fieldIssueTypesType.isRequired,
  fieldIssueTypes: PropTypes.array,
  fieldIssueSubTypes: PropTypes.array,
  currentSheet: sheetObjectType,
  focusForm: PropTypes.bool,
  users: PropTypes.array,
  groupedProjectEntities: PropTypes.array,
  uploadingAttachments: PropTypes.array,
  isUploadingAttachments: PropTypes.bool,
  container: PropTypes.object,
  issueCustomAttributesMappings: PropTypes.object,
  activeIssueRootCausesGroupedDropdownOptions: PropTypes.array,
  rootCauseEntitiesMap: PropTypes.object,
  issueCustomAttributes: PropTypes.object,
  isCreateFormDeepLinking: PropTypes.bool.isRequired,
  issueTypesAndSubTypesTitleMap: idToTitleMapType.isRequired,
  additionalAttributes: PropTypes.object,
  initialFieldValues: PropTypes.object,
  localStoragePrefix: PropTypes.string,
  defaultIssueSubtypeId: PropTypes.string,
  isDateSelectorMigrationEnabled: PropTypes.bool,
  systemDefaultIssueSubtypeId: PropTypes.string,
  formValuesFromTemplate: PropTypes.object,
  anyActiveIssueTemplates: PropTypes.bool,
  requiredFieldsMap: PropTypes.object.isRequired,
  availableStatuses: PropTypes.array,
  titleFieldLabel: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  locationsTreeNodesDictionary: PropTypes.object.isRequired,
  updateDocumentDetails: PropTypes.bool.isRequired
};

export default IssueCreateForm;
